import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';

export default function Hero({ scrollToSection }) {
  return (
    <section
      id="forside"
      className="relative min-h-[80vh] lg:min-h-[70vh] xl:min-h-[65vh] flex items-center bg-[#F7F4F1] pt-24 md:pt-28 pb-12 md:pb-16 overflow-hidden"
    >
      {/* Soft organic background shapes */}
      <div className="absolute top-[-10%] right-[-5%] w-[50vw] h-[50vw] rounded-full bg-[#EDE6DF] blur-[120px] opacity-60 pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-[#BFC8BE]/20 blur-[100px] opacity-50 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12 w-full relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Text Content */}
          <div className="md:col-span-6 lg:col-span-7 flex flex-col items-start text-left">
            <motion.h1
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="font-serif-elegant text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#4A4A4A] leading-[1.15] mb-4 font-light"
            >
              Et trygt rum til <br />
              <span className="italic text-[#BFC8BE]">refleksion</span>, udvikling <br />
              og balance.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="font-sans-clean text-sm md:text-base text-[#4A4A4A]/70 max-w-xl leading-relaxed mb-6"
            >
              Når livet udfordrer, eller du ønsker en dybere forståelse af dig selv, tilbyder jeg professionel og nærværende psykologhjælp tilpasset dine behov.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto"
            >
              <button
                onClick={() => scrollToSection('kontakt')}
                className="px-6 py-3 bg-[#BFC8BE] hover:bg-[#BFC8BE]/90 text-white font-sans-clean text-xs tracking-widest uppercase rounded-sm transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 group"
              >
                Book samtale
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>
          </div>

          {/* Image Content */}
          <div className="md:col-span-6 lg:col-span-5 relative flex justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="relative w-full max-w-[440px] aspect-[3/4] rounded-t-[100px] rounded-b-[30px] overflow-hidden shadow-xl"
            >
              <img
                src="https://assets.auxodev.dk/assets/4291744f-0a58-4691-8a7f-39ecbae78b95/45ab40ce-ffe4-4645-9b62-523934f3779b.jpg"
                alt="Marehalm og sand ved havet i blødt lys"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4A4A4A]/10 to-transparent pointer-events-none" />
            </motion.div>

            {/* Decorative organic badge */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="absolute bottom-4 left-[-15px] sm:left-[-5px] bg-white p-4 rounded-xl shadow-md max-w-[160px] border border-white/40 hidden sm:block"
            >
              <p className="font-serif-elegant italic text-base text-[#4A4A4A] leading-tight mb-1">
                "Nærvær & faglighed"
              </p>
              <p className="font-sans-clean text-[9px] tracking-wider uppercase text-[#4A4A4A]/60">
                Individuelle samtaler i trygge rammer
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom wave/curve separator */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-[#EDE6DF]/30 pointer-events-none" />
    </section>
  );
}
